import { useState } from "react";
import { Link } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Percent,
  Briefcase,
  CheckCircle2,
  Banknote,
  Clock,
  XCircle,
  Filter,
  Calendar,
  Users,
} from "lucide-react";
import { toast } from "sonner";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function formatCompact(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n) || n === 0) return "R$ 0";
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (Math.abs(n) >= 1_000) return `R$ ${(n / 1_000).toFixed(1).replace(".", ",")}k`;
  return `R$ ${n.toFixed(0)}`;
}
function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const STATUS_CFG: Record<string, { label: string; tint: string; icon: any }> = {
  pending: {
    label: "Pendente",
    tint: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
    icon: Clock,
  },
  approved: {
    label: "Aprovada",
    tint: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300",
    icon: CheckCircle2,
  },
  paid: {
    label: "Paga",
    tint: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
    icon: Banknote,
  },
  cancelled: {
    label: "Cancelada",
    tint: "bg-muted text-muted-foreground",
    icon: XCircle,
  },
};

type StatusFilter = "all" | "pending" | "approved" | "paid" | "cancelled";

function CrmCommissionsContent() {
  const { can } = usePermissions();
  const canApprove = can("crm:commissions_approve");
  const canViewAll = can("crm:commissions_view_all");

  const now = new Date();
  const [status, setStatus] = useState<StatusFilter>("all");
  const [userId, setUserId] = useState<string>("all");
  const [month, setMonth] = useState<string>(String(now.getMonth() + 1));
  const [year, setYear] = useState<string>(String(now.getFullYear()));

  const [actionTarget, setActionTarget] = useState<
    { id: number; action: "approve" | "pay" | "cancel" } | null
  >(null);

  const sellersQuery = trpc.crm.sellers.list.useQuery(
    canViewAll ? {} : (undefined as any),
    { enabled: canViewAll },
  );

  const listQuery = trpc.crm.commissions.list.useQuery({
    status: status === "all" ? undefined : (status as any),
    userId: canViewAll && userId !== "all" ? Number(userId) : undefined,
    referenceMonth: month === "all" ? undefined : Number(month),
    referenceYear: Number(year),
    limit: 200,
  });

  const summaryQuery = trpc.crm.commissions.summary.useQuery({
    userId: canViewAll && userId !== "all" ? Number(userId) : undefined,
    referenceMonth: month === "all" ? undefined : Number(month),
    referenceYear: Number(year),
  });

  const utils = trpc.useUtils();
  const approve = trpc.crm.commissions.approve.useMutation({
    onSuccess: () => {
      toast.success("Comissão aprovada");
      utils.crm.commissions.list.invalidate();
      utils.crm.commissions.summary.invalidate();
    },
  });
  const markPaid = trpc.crm.commissions.markPaid.useMutation({
    onSuccess: () => {
      toast.success("Marcada como paga");
      utils.crm.commissions.list.invalidate();
      utils.crm.commissions.summary.invalidate();
    },
  });
  const cancel = trpc.crm.commissions.cancel.useMutation({
    onSuccess: () => {
      toast.success("Comissão cancelada");
      utils.crm.commissions.list.invalidate();
      utils.crm.commissions.summary.invalidate();
    },
  });

  const rows = listQuery.data ?? [];
  const summary = summaryQuery.data;
  const sellers = sellersQuery.data ?? [];

  const MONTHS = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
  ];

  const years = Array.from({ length: 5 }).map((_, i) => now.getFullYear() - i);

  function confirmAction() {
    if (!actionTarget) return;
    if (actionTarget.action === "approve") approve.mutate({ id: actionTarget.id });
    else if (actionTarget.action === "pay") markPaid.mutate({ id: actionTarget.id });
    else if (actionTarget.action === "cancel") cancel.mutate({ id: actionTarget.id });
    setActionTarget(null);
  }

  return (
    <div className="space-y-5">
      {/* ── Header ──────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Percent className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">
              Comissões
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              {canViewAll
                ? "Comissões da equipe — aprove, marque como paga ou cancele."
                : "Suas comissões acumuladas."}
            </p>
          </div>
        </div>
      </div>

      {/* ── KPIs de totais ──────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Pendentes"
          value={formatCompact(summary?.pending ?? 0)}
          icon={Clock}
          tint="amber"
        />
        <KpiCard
          label="Aprovadas"
          value={formatCompact(summary?.approved ?? 0)}
          icon={CheckCircle2}
          tint="sky"
        />
        <KpiCard
          label="Pagas"
          value={formatCompact(summary?.paid ?? 0)}
          icon={Banknote}
          tint="emerald"
        />
        <KpiCard
          label="Total"
          value={formatCompact(summary?.total ?? 0)}
          icon={Percent}
          tint="primary"
        />
      </div>

      {/* ── Filtros ─────────────────────────────── */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="flex items-center gap-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={(v: any) => setStatus(v)}>
              <SelectTrigger className="h-8 w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendentes</SelectItem>
                <SelectItem value="approved">Aprovadas</SelectItem>
                <SelectItem value="paid">Pagas</SelectItem>
                <SelectItem value="cancelled">Canceladas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Período
            </Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-8 w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Ano todo</SelectItem>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i} value={String(i + 1)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-8 w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {canViewAll && (
            <div className="flex items-center gap-1.5">
              <Label className="text-xs flex items-center gap-1">
                <Users className="h-3 w-3" />
                Vendedor
              </Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger className="h-8 w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {sellers.map((s: any) => (
                    <SelectItem key={s.profile.userId} value={String(s.profile.userId)}>
                      {s.user.name ?? s.user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Lista ───────────────────────────────── */}
      {listQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Percent className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <h3 className="font-semibold mb-1">Nenhuma comissão</h3>
            <p className="text-sm text-muted-foreground">
              Ajuste os filtros ou espere o próximo deal ser ganho.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((row: any) => {
            const cfg = STATUS_CFG[row.commission.status] ?? STATUS_CFG.pending;
            const Icon = cfg.icon;
            return (
              <Card
                key={row.commission.id}
                className="transition hover:shadow-sm"
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div
                      className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${cfg.tint}`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="secondary" className={cfg.tint}>
                          {cfg.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {String(row.commission.referenceMonth).padStart(2, "0")}/
                          {row.commission.referenceYear}
                        </span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs tabular-nums">
                          {Number(row.commission.pct).toFixed(2)}% sobre{" "}
                          {formatBRL(row.commission.baseValue)}
                        </span>
                      </div>
                      {row.deal ? (
                        <Link
                          href={`/crm/deals/${row.deal.id}`}
                          className="text-sm font-semibold hover:text-primary truncate block"
                        >
                          <Briefcase className="h-3.5 w-3.5 inline mr-1" />
                          {row.deal.title}
                        </Link>
                      ) : (
                        <div className="text-sm font-semibold text-muted-foreground">
                          Deal removido
                        </div>
                      )}
                      {canViewAll && row.user && (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                          <Avatar className="h-5 w-5">
                            <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                              {initials(row.user.name)}
                            </AvatarFallback>
                          </Avatar>
                          {row.user.name ?? row.user.email}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold tabular-nums">
                        {formatBRL(row.commission.amount)}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {row.commission.paidAt
                          ? `paga em ${new Date(
                              row.commission.paidAt,
                            ).toLocaleDateString("pt-BR")}`
                          : row.commission.approvedAt
                          ? `aprovada em ${new Date(
                              row.commission.approvedAt,
                            ).toLocaleDateString("pt-BR")}`
                          : `criada em ${new Date(
                              row.commission.createdAt,
                            ).toLocaleDateString("pt-BR")}`}
                      </div>
                    </div>
                  </div>

                  {canApprove && row.commission.status !== "cancelled" && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                      {row.commission.status === "pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setActionTarget({
                              id: row.commission.id,
                              action: "approve",
                            })
                          }
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                          Aprovar
                        </Button>
                      )}
                      {row.commission.status === "approved" && (
                        <Button
                          size="sm"
                          onClick={() =>
                            setActionTarget({
                              id: row.commission.id,
                              action: "pay",
                            })
                          }
                        >
                          <Banknote className="h-3.5 w-3.5 mr-1.5" />
                          Marcar como paga
                        </Button>
                      )}
                      {row.commission.status !== "paid" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setActionTarget({
                              id: row.commission.id,
                              action: "cancel",
                            })
                          }
                        >
                          Cancelar
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!actionTarget} onOpenChange={(v) => !v && setActionTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionTarget?.action === "approve" && "Aprovar comissão?"}
              {actionTarget?.action === "pay" && "Marcar como paga?"}
              {actionTarget?.action === "cancel" && "Cancelar comissão?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionTarget?.action === "approve" &&
                "A comissão ficará pronta para pagamento."}
              {actionTarget?.action === "pay" &&
                "Registre o pagamento. Esta ação não pode ser revertida no app."}
              {actionTarget?.action === "cancel" &&
                "A comissão será marcada como cancelada e não entrará nos totais."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction}
              className={
                actionTarget?.action === "cancel"
                  ? "bg-destructive hover:bg-destructive/90"
                  : ""
              }
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  tint = "default",
}: {
  label: string;
  value: string;
  icon: any;
  tint?: "default" | "amber" | "sky" | "emerald" | "primary";
}) {
  const tintMap = {
    default: "bg-muted text-muted-foreground",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    sky: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    primary: "bg-primary/10 text-primary",
  };
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {label}
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums truncate">{value}</div>
          </div>
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tintMap[tint]}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CrmCommissions() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:commissions_view">
        <CrmCommissionsContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
